import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { ApplicationsGrid } from "../components/ApplicationsGrid";
import {
  ApplicationWizard,
  JobApplication,
} from "../components/ApplicationWizard";
import { JobApplicationForm } from "../components/JobApplicationForm";
import { NotesPanel } from "../components/NotesPanel";
import { InterviewPrepPanel } from "../components/InterviewPrepPanel";
import { CompanyIntelligencePanel } from "../components/CompanyIntelligencePanel";
import { ResumeAssignmentModal } from "../components/ResumeAssignmentModal";
import { CommunicationCoachPanel } from "../components/CommunicationCoachPanel";
import { LoadingSpinner } from "../components/LoadingSpinner";
import {
  jobApplicationService,
  ApplicationNote,
  JOB_APPLICATIONS_FETCH_LIMIT,
} from "../services/jobApplicationService";
import { useLoading } from "../contexts/LoadingContext";
import { useSubscription } from "../hooks/useSubscription";
import { useMessageBox } from "../components/MessageBox";
import { useResumes } from "../hooks/useResumes";
import { IntelligentSearchResult } from "../services/intelligentSearchService";
import { IntelligentSearchModal } from "../components/IntelligentSearchModal";
import toast from "react-hot-toast";
import { sendDebugLog } from "../utils/debugLogging";
// WebSocket removed - using localStorage-based cross-tab refresh instead
import "./ApplicationsPage.css";

export const ApplicationsPage: React.FC = () => {
  // #region agent log APPLICATIONS_PAGE_MOUNT
  useEffect(() => {
    const logData = {
      sessionId: "debug-session",
      runId: "run10",
      hypothesisId: "A",
      location: "ApplicationsPage.tsx:19",
      message: "ApplicationsPage component mounted",
      data: {
        hostname:
          typeof window !== "undefined" ? window.location.hostname : "N/A",
        pathname:
          typeof window !== "undefined" ? window.location.pathname : "N/A",
      },
      timestamp: Date.now(),
    };
    sendDebugLog(logData);

    // Log unmount
    return () => {
      const unmountLog = {
        sessionId: "debug-session",
        runId: "run10",
        hypothesisId: "A",
        location: "ApplicationsPage.tsx:19",
        message: "ApplicationsPage component unmounting",
        data: {
          hostname:
            typeof window !== "undefined" ? window.location.hostname : "N/A",
        },
        timestamp: Date.now(),
      };
      sendDebugLog(unmountLog);
    };
  }, []);
  // #endregion

  // #region agent log PAGE_RELOAD_DETECTION
  // Catch page unload events (will fire on reload)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleBeforeUnload = () => {
      sendDebugLog({
        sessionId: "debug-session",
        runId: "run5",
        hypothesisId: "L",
        location: "ApplicationsPage.tsx:59",
        message: "beforeunload event fired - page is reloading",
        data: {
          stack: new Error().stack?.substring(0, 500),
        },
        timestamp: Date.now(),
      });
    };

    const handleUnload = () => {
      sendDebugLog({
        sessionId: "debug-session",
        runId: "run5",
        hypothesisId: "L",
        location: "ApplicationsPage.tsx:76",
        message: "unload event fired - page is unloading",
        data: {},
        timestamp: Date.now(),
      });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, []);
  // #endregion

  // #region agent log GLOBAL_ERROR_HANDLER
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      sendDebugLog({
        sessionId: "debug-session",
        runId: "run5",
        hypothesisId: "V",
        location: "ApplicationsPage.tsx:90",
        message: "Global error caught",
        data: {
          errorMessage: event.message,
          errorFilename: event.filename,
          errorLineno: event.lineno,
          errorColno: event.colno,
          errorStack: event.error?.stack?.substring(0, 500),
        },
        timestamp: Date.now(),
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      sendDebugLog({
        sessionId: "debug-session",
        runId: "run5",
        hypothesisId: "V",
        location: "ApplicationsPage.tsx:110",
        message: "Unhandled promise rejection",
        data: {
          reason: event.reason?.toString(),
          errorStack:
            event.reason instanceof Error
              ? event.reason.stack?.substring(0, 500)
              : undefined,
        },
        timestamp: Date.now(),
      });
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
    };
  }, []);
  // #endregion

  // #region agent log COMPONENT_RENDER_TRACKING
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;
  if (renderCountRef.current > 10) {
    sendDebugLog({
      sessionId: "debug-session",
      runId: "run5",
      hypothesisId: "P",
      location: "ApplicationsPage.tsx:95",
      message: "Excessive renders detected",
      data: {
        renderCount: renderCountRef.current,
        timestamp: Date.now(),
      },
      timestamp: Date.now(),
    });
  }
  // #endregion

  const { setLoading, loadingStates } = useLoading();
  const { showMessageBox } = useMessageBox();
  const {
    subscription,
    getRemainingQuotas,
    reload: reloadSubscription,
  } = useSubscription();

  // #region agent log SUBSCRIPTION_CHANGES
  useEffect(() => {
    sendDebugLog({
      sessionId: "debug-session",
      runId: "run10",
      hypothesisId: "B",
      location: "ApplicationsPage.tsx:166",
      message: "Subscription changed in ApplicationsPage",
      data: {
        hasSubscription: !!subscription,
        currentPlanId: subscription?.currentPlanId,
        status: subscription?.status,
        subscriptionObjectId: subscription
          ? Object.keys(subscription).join(",")
          : "null",
      },
      timestamp: Date.now(),
    });
  }, [subscription]);
  // #endregion
  const navigate = useNavigate();
  const resumesHook = useResumes();
  const { resumes, isLoading: resumesLoading } = resumesHook;
  const hasResume = (resumes?.length ?? 0) > 0;
  const addApplicationGated = !hasResume && !resumesLoading;
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [editingApplication, setEditingApplication] =
    useState<JobApplication | null>(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [notesPanelOpen, setNotesPanelOpen] = useState(false);
  const [interviewPrepPanelOpen, setInterviewPrepPanelOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] =
    useState<JobApplication | null>(null);
  const [selectedApplicationForPrep, setSelectedApplicationForPrep] =
    useState<JobApplication | null>(null);
  const [selectedApplicationForResume, setSelectedApplicationForResume] =
    useState<JobApplication | null>(null);
  const [isResumeAssignmentOpen, setIsResumeAssignmentOpen] = useState(false);
  const [isCommunicationCoachOpen, setIsCommunicationCoachOpen] =
    useState(false);
  const [selectedApplicationForCoach, setSelectedApplicationForCoach] =
    useState<JobApplication | null>(null);
  const [companyIntelligencePanelOpen, setCompanyIntelligencePanelOpen] =
    useState(false);
  const [
    selectedApplicationForCompanyIntel,
    setSelectedApplicationForCompanyIntel,
  ] = useState<JobApplication | null>(null);
  const [
    shouldReopenEditAfterCompanyIntel,
    setShouldReopenEditAfterCompanyIntel,
  ] = useState(false);

  const [intelligentSearchResults, setIntelligentSearchResults] =
    useState<IntelligentSearchResult | null>(null);
  const [showIntelligentSearchModal, setShowIntelligentSearchModal] =
    useState<boolean>(false);
  const [intelligentSearchLoading, _setIntelligentSearchLoading] =
    useState<boolean>(false);
  const [intelligentSearchError, setIntelligentSearchError] = useState<
    string | null
  >(null);
  const [applicationsTruncated, setApplicationsTruncated] =
    useState<boolean>(false);
  const [isInitialApplicationsLoading, setIsInitialApplicationsLoading] =
    useState<boolean>(true);

  // Calculate remaining quotas
  const remainingQuotas = useMemo(() => {
    return getRemainingQuotas({
      resumes: resumes.length,
      jobApplications: applications.length,
    });
  }, [resumes.length, applications.length, getRemainingQuotas]);

  // Load applications
  const loadApplications = useCallback(
    async (showLoading: boolean = true) => {
      // #region agent log LOAD_APPLICATIONS_START
      sendDebugLog({
        sessionId: "debug-session",
        runId: "run5",
        hypothesisId: "A",
        location: "ApplicationsPage.tsx:145",
        message: "loadApplications called",
        data: {
          showLoading,
          callStack: new Error().stack?.substring(0, 500),
          renderCount: renderCountRef.current,
          hasSelectedAppForPrep: !!selectedApplicationForPrep,
          selectedAppId: selectedApplicationForPrep?.id,
        },
        timestamp: Date.now(),
      });
      // #endregion
      try {
        if (showLoading) {
          setLoading("applications", true);
        }
        const response = await jobApplicationService.getApplications();
        // Support both { applications } and { data }; always show list regardless of resume presence
        const rawList = Array.isArray(response?.applications)
          ? response.applications
          : Array.isArray((response as any)?.data)
            ? (response as any).data
            : [];
        const isTruncated = Boolean((response as any)?.pagination?.has_more);
        const apps = rawList.map((app: any) =>
          jobApplicationService.convertToJobApplication(app),
        );

        // #region agent log LOAD_APPLICATIONS_BEFORE_SET
        sendDebugLog({
          sessionId: "debug-session",
          runId: "run11",
          hypothesisId: "H3",
          location: "ApplicationsPage.tsx:loadApplications-before-set",
          message: "About to setApplications with new apps array",
          data: {
            appsCount: apps.length,
            hasSelectedAppForPrep: !!selectedApplicationForPrep,
            selectedAppId: selectedApplicationForPrep?.id,
            selectedAppInNewArray: apps.find(
              (a: JobApplication) => a.id === selectedApplicationForPrep?.id,
            )
              ? true
              : false,
          },
          timestamp: Date.now(),
        });
        // #endregion

        setApplications(apps);
        setApplicationsTruncated(isTruncated);
      } catch (error) {
        // #region agent log LOAD_APPLICATIONS_ERROR
        sendDebugLog({
          sessionId: "debug-session",
          runId: "run5",
          hypothesisId: "I",
          location: "ApplicationsPage.tsx:177",
          message: "loadApplications error caught",
          data: {
            errorType:
              error instanceof Error ? error.constructor.name : typeof error,
            errorMessage:
              error instanceof Error ? error.message : String(error),
            errorString: String(error),
            errorStack:
              error instanceof Error
                ? error.stack?.substring(0, 500)
                : undefined,
            renderCount: renderCountRef.current,
          },
          timestamp: Date.now(),
        });
        // #endregion
        // Set empty array on error to prevent UI issues
        setApplications([]);
        setApplicationsTruncated(false);
      } finally {
        if (showLoading) {
          setIsInitialApplicationsLoading(false);
        }
        if (showLoading) {
          setLoading("applications", false);
        }
      }
    },
    [setLoading],
  );

  // #region agent log LOAD_APPLICATIONS_CALLBACK_TRACKING
  const loadApplicationsRef = useRef(loadApplications);
  const setLoadingRef = useRef(setLoading);
  useEffect(() => {
    const loadApplicationsChanged =
      loadApplicationsRef.current !== loadApplications;
    const setLoadingChanged = setLoadingRef.current !== setLoading;
    if (loadApplicationsChanged || setLoadingChanged) {
      sendDebugLog({
        sessionId: "debug-session",
        runId: "run5",
        hypothesisId: "Q",
        location: "ApplicationsPage.tsx:210",
        message: "loadApplications or setLoading function changed",
        data: {
          loadApplicationsChanged,
          setLoadingChanged,
          renderCount: renderCountRef.current,
        },
        timestamp: Date.now(),
      });
      loadApplicationsRef.current = loadApplications;
      setLoadingRef.current = setLoading;
    }
  }, [loadApplications, setLoading]);
  // #endregion

  // Load applications on mount
  useEffect(() => {
    // #region agent log LOAD_APPLICATIONS_EFFECT
    sendDebugLog({
      sessionId: "debug-session",
      runId: "run5",
      hypothesisId: "M",
      location: "ApplicationsPage.tsx:232",
      message: "useEffect for loadApplications triggered",
      data: {
        loadApplicationsFunction: typeof loadApplications,
        renderCount: renderCountRef.current,
      },
      timestamp: Date.now(),
    });
    // #endregion
    loadApplications();
  }, [loadApplications]);

  // Cross-tab refresh using localStorage events + polling fallback
  useEffect(() => {
    // Handle storage events from OTHER tabs (cross-tab communication)
    // Note: localStorage events only fire in OTHER tabs, not the tab that made the change
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "job_application_updated" && e.newValue) {
        loadApplications(false); // Don't show loading for automatic refresh
      }
    };

    // Handle custom event from extension content script (instant refresh)
    // Extension → Content Script → Custom Event → Main App
    const handleExtensionNotification = (_e: Event) => {
      loadApplications(false); // Don't show loading for automatic refresh
    };

    // Listen for storage events (cross-tab communication)
    window.addEventListener("storage", handleStorageChange);

    // Listen for custom event from extension (instant refresh)
    window.addEventListener(
      "kairos-job-application-saved",
      handleExtensionNotification as EventListener,
    );

    // Polling fallback: Refresh every 30 seconds when tab is visible
    // This catches:
    // 1. Extension → Main app (different origins, localStorage events don't work)
    // 2. Cases where localStorage events might be missed
    let pollInterval: NodeJS.Timeout | null = null;
    const startPolling = () => {
      if (document.visibilityState === "visible") {
        pollInterval = setInterval(() => {
          loadApplications(false); // Don't show loading for automatic refresh
        }, 30000); // Poll every 30 seconds when tab is visible
      }
    };

    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    // Start/stop polling based on tab visibility
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Refresh immediately when tab becomes visible, then start polling
        loadApplications(false); // Don't show loading for automatic refresh
        startPolling();
      } else {
        stopPolling();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Start polling if tab is already visible
    if (document.visibilityState === "visible") {
      startPolling();
    }

    // Cleanup
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "kairos-job-application-saved",
        handleExtensionNotification as EventListener,
      );
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopPolling();
    };
  }, [loadApplications]);

  // Application management functions
  const handleAddApplication = useCallback(() => {
    if (addApplicationGated) {
      navigate("/resumes");
      return;
    }
    setEditingApplication(null);
    setIsViewMode(false);
    setIsWizardOpen(true);
  }, [addApplicationGated, navigate]);

  const handleEditApplication = useCallback(
    async (application: JobApplication) => {
      if (!application.id) return;
      setShouldReopenEditAfterCompanyIntel(false);
      // Close other panels when opening edit form - completely isolate edit functionality
      setInterviewPrepPanelOpen(false);
      setSelectedApplicationForPrep(null);
      setIsResumeAssignmentOpen(false);
      setSelectedApplicationForResume(null);
      setIsCommunicationCoachOpen(false);
      setSelectedApplicationForCoach(null);
      try {
        // Try to fetch full application details with notes
        // NOTE: This is ONLY for editing - no interview prep or other features
        const fullApplicationResponse =
          await jobApplicationService.getApplication(application.id, true);
        if (fullApplicationResponse) {
          const fullApplication = jobApplicationService.convertToJobApplication(
            fullApplicationResponse,
          );
          setEditingApplication(fullApplication);
        } else {
          // Fallback to using the application from the grid if fetch returns null
          setEditingApplication(application);
        }
      } catch (error) {
        toast.error("Failed to load application details");
        // Fallback to using the application from the grid if fetch fails
        setEditingApplication(application);
      }
      setIsViewMode(false);
      setIsEditFormOpen(true);
    },
    [],
  );

  const handleResumeAssignment = useCallback(
    (application: JobApplication) => {
      if (addApplicationGated) {
        showMessageBox({
          type: "info",
          title: "Resume required",
          message: "Upload a resume to enable this feature.",
          buttons: ["ok"],
          onButtonClick: () => {},
        });
        return;
      }
      if (!application.id) {
        showMessageBox({
          type: "error",
          title: "Error",
          message: "Application ID is required to assign a resume.",
          buttons: ["ok"],
          onButtonClick: () => {},
        });
        return;
      }
      setSelectedApplicationForResume(application);
      setIsResumeAssignmentOpen(true);
    },
    [addApplicationGated, showMessageBox],
  );

  const handleCommunicationCoach = useCallback(
    (application: JobApplication) => {
      if (!application.id) {
        showMessageBox({
          type: "error",
          title: "Error",
          message: "Application ID is required for Communication Coach.",
          buttons: ["ok"],
          onButtonClick: () => {},
        });
        return;
      }
      // Close other panels when opening communication coach
      setInterviewPrepPanelOpen(false);
      setSelectedApplicationForPrep(null);
      setIsEditFormOpen(false);
      setEditingApplication(null);
      setIsResumeAssignmentOpen(false);
      setSelectedApplicationForResume(null);
      setSelectedApplicationForCoach(application);
      setIsCommunicationCoachOpen(true);
    },
    [showMessageBox],
  );

  const handleCloseCommunicationCoach = useCallback(() => {
    setIsCommunicationCoachOpen(false);
    setSelectedApplicationForCoach(null);
  }, []);

  const handleViewApplication = useCallback((application: JobApplication) => {
    // #region agent log SET_SELECTED_APPLICATION_FOR_PREP
    sendDebugLog({
      sessionId: "debug-session",
      runId: "run7",
      hypothesisId: "Y",
      location: "ApplicationsPage.tsx:451",
      message: "Setting selectedApplicationForPrep",
      data: {
        applicationId: application.id,
        applicationCompany: application.company,
        applicationPosition: application.position,
      },
      timestamp: Date.now(),
    });
    // #endregion
    // Open interview prep panel instead of wizard
    setSelectedApplicationForPrep(application);
    setInterviewPrepPanelOpen(true);
  }, []);

  const mergeDraftIntoApplication = useCallback(
    (base: JobApplication, draft?: Partial<JobApplication>): JobApplication => {
      if (!draft) {
        return base;
      }
      const merged = { ...base } as JobApplication;
      Object.entries(draft).forEach(([key, value]) => {
        if (value !== undefined) {
          (merged as any)[key] = value;
        }
      });
      return merged;
    },
    [],
  );

  const handleOpenCompanyIntelligence = useCallback(
    (application: JobApplication) => {
      setShouldReopenEditAfterCompanyIntel(false);
      setSelectedApplicationForCompanyIntel(application);
      setCompanyIntelligencePanelOpen(true);
    },
    [],
  );

  const handleOpenCompanyIntelligenceFromEdit = useCallback(
    (draft?: Partial<JobApplication>) => {
      if (!editingApplication) return;
      const mergedApplication = mergeDraftIntoApplication(
        editingApplication,
        draft,
      );
      setEditingApplication(mergedApplication);
      setSelectedApplicationForCompanyIntel(mergedApplication);
      setCompanyIntelligencePanelOpen(true);
      setShouldReopenEditAfterCompanyIntel(true);
      setIsEditFormOpen(false);
    },
    [editingApplication, mergeDraftIntoApplication],
  );

  // #region agent log SELECTED_APP_FOR_PREP_CHANGES
  useEffect(() => {
    sendDebugLog({
      sessionId: "debug-session",
      runId: "run11",
      hypothesisId: "H1",
      location: "ApplicationsPage.tsx:selectedApplicationForPrep-effect",
      message: "selectedApplicationForPrep changed",
      data: {
        applicationId: selectedApplicationForPrep?.id,
        applicationCompany: selectedApplicationForPrep?.company,
        applicationPosition: selectedApplicationForPrep?.position,
        isNull: selectedApplicationForPrep === null,
        renderCount: renderCountRef.current,
      },
      timestamp: Date.now(),
    });

    // Check if we can find this application in the current applications array
    if (selectedApplicationForPrep?.id) {
      const appInArray = applications.find(
        (a) => a.id === selectedApplicationForPrep.id,
      );
      sendDebugLog({
        sessionId: "debug-session",
        runId: "run11",
        hypothesisId: "H2",
        location: "ApplicationsPage.tsx:selectedApplicationForPrep-effect",
        message: "Comparing selectedApplicationForPrep with applications array",
        data: {
          applicationId: selectedApplicationForPrep.id,
          foundInArray: !!appInArray,
          isSameObject: appInArray === selectedApplicationForPrep,
          arrayLength: applications.length,
        },
        timestamp: Date.now(),
      });
    }
  }, [selectedApplicationForPrep, applications]);

  // Sync selectedApplicationForPrep with applications array when it updates
  // This keeps the data in sync but InterviewPrepPanel will skip refetch if already completed
  // NOTE: This only syncs when interview prep panel is open - completely isolated from edit form
  useEffect(() => {
    // Only sync if interview prep panel is open AND edit form is NOT open
    // This ensures complete isolation between edit and interview prep
    if (
      selectedApplicationForPrep?.id &&
      interviewPrepPanelOpen &&
      !isEditFormOpen &&
      applications.length > 0
    ) {
      const appInArray = applications.find(
        (a) => a.id === selectedApplicationForPrep.id,
      );
      if (appInArray && appInArray !== selectedApplicationForPrep) {
        sendDebugLog({
          sessionId: "debug-session",
          runId: "run11",
          hypothesisId: "H4",
          location: "ApplicationsPage.tsx:sync-selected-app-effect",
          message:
            "Syncing selectedApplicationForPrep with applications array (edit form closed)",
          data: {
            applicationId: selectedApplicationForPrep.id,
            isSameObject: appInArray === selectedApplicationForPrep,
            oldCompany: selectedApplicationForPrep.company,
            newCompany: appInArray.company,
            isEditFormOpen,
          },
          timestamp: Date.now(),
        });
        // Update to keep data in sync, but InterviewPrepPanel will skip refetch if already completed
        setSelectedApplicationForPrep(appInArray);
      }
    }
  }, [
    applications,
    interviewPrepPanelOpen,
    selectedApplicationForPrep,
    isEditFormOpen,
  ]);
  // #endregion

  const handleDeleteApplication = useCallback(
    async (id: string) => {
      try {
        await jobApplicationService.deleteApplication(id);
        toast.success("Application deleted successfully");
        await loadApplications(false);
      } catch (error) {
        toast.error("Failed to delete application");
      }
    },
    [loadApplications],
  );

  const handleUpdateStatus = useCallback(
    async (id: string, newStatus: JobApplication["status"]) => {
      setLoading("applications", true);
      try {
        await jobApplicationService.updateApplication(id, {
          status: newStatus,
        });
        toast.success("Application status updated successfully");
        await loadApplications(false);
      } catch (error) {
        toast.error("Failed to update application status");
        // Reload on error to revert any optimistic updates
        await loadApplications();
      } finally {
        setLoading("applications", false);
      }
    },
    [setLoading, loadApplications],
  );

  const handleSaveApplication = useCallback(
    async (application: JobApplication) => {
      setLoading("applications", true);
      try {
        // Trigger cross-tab refresh notification (for other tabs)
        localStorage.setItem("job_application_updated", Date.now().toString());

        if (application.id) {
          await jobApplicationService.updateApplication(
            application.id,
            application,
          );
          toast.success("Application updated successfully");
        } else {
          await jobApplicationService.createApplication(application);
          toast.success("Application created successfully");
        }
        setIsWizardOpen(false);
        setIsEditFormOpen(false);
        setEditingApplication(null); // Clear editing state completely

        // Refresh applications list (same-tab refresh)
        // NOTE: This will NOT affect interview prep panel if it's open - it's completely isolated
        await loadApplications(false);

        // Reload subscription after 2 seconds (for token balance updates)
        setTimeout(() => {
          reloadSubscription();
        }, 2000);

        // Trigger cross-tab refresh notification again
        localStorage.setItem("job_application_updated", Date.now().toString());
      } catch (error) {
        toast.error("Failed to save application");
      } finally {
        setLoading("applications", false);
      }
    },
    [loadApplications, setLoading, reloadSubscription],
  );

  const handleCloseWizard = useCallback(() => {
    setIsWizardOpen(false);
    setEditingApplication(null);
    setIsViewMode(false);
  }, []);

  const handleCloseEditForm = useCallback(() => {
    setIsEditFormOpen(false);
    setEditingApplication(null);
    setIsViewMode(false);
    setShouldReopenEditAfterCompanyIntel(false);
  }, []);

  // Notes panel handlers
  const handleOpenNotes = useCallback((application: JobApplication) => {
    setSelectedApplication(application);
    setNotesPanelOpen(true);
  }, []);

  const handleCloseNotes = useCallback(() => {
    setNotesPanelOpen(false);
    setSelectedApplication(null);
    // Reload applications to update notes_count
    loadApplications();
  }, [loadApplications]);

  const handleLoadNotes = useCallback(
    async (applicationId: string): Promise<ApplicationNote[]> => {
      try {
        const application = await jobApplicationService.getApplication(
          applicationId,
          true,
        );
        return application?.notes || [];
      } catch (error) {
        toast.error("Failed to load notes");
        return [];
      }
    },
    [],
  );

  const handleSaveNote = useCallback(
    async (note: ApplicationNote): Promise<void> => {
      if (!selectedApplication?.id) return;

      setLoading("applications", true);
      try {
        await jobApplicationService.addNote(selectedApplication.id, note);
        await loadApplications(false);
      } catch (error) {
        throw error;
      } finally {
        setLoading("applications", false);
      }
    },
    [selectedApplication, setLoading, loadApplications],
  );

  const handleUpdateNote = useCallback(
    async (noteId: string, note: ApplicationNote): Promise<void> => {
      if (!selectedApplication?.id) return;

      setLoading("applications", true);
      try {
        await jobApplicationService.updateNote(
          selectedApplication.id,
          noteId,
          note,
        );
        await loadApplications(false);
      } catch (error) {
        toast.error("Failed to update note");
        throw error;
      } finally {
        setLoading("applications", false);
      }
    },
    [selectedApplication, setLoading, loadApplications],
  );

  const handleDeleteNote = useCallback(
    async (noteId: string): Promise<void> => {
      if (!selectedApplication?.id) return;

      setLoading("applications", true);
      try {
        await jobApplicationService.deleteNote(selectedApplication.id, noteId);
        await loadApplications(false);
      } catch (error) {
        toast.error("Failed to delete note");
        throw error;
      } finally {
        setLoading("applications", false);
      }
    },
    [selectedApplication, setLoading, loadApplications],
  );

  // handleIntelligentSearch removed - feature temporarily disabled
  // const handleIntelligentSearch = useCallback(async (application: JobApplication) => { ... }, [])

  return (
    <div className="applications-page">
      <div className="page-header" data-tour-id="applications-header">
        <div className="header-content">
          <h1 className="page-title">Job Applications</h1>
          <p className="page-subtitle">
            Manage and track your job applications
          </p>
        </div>
        <div className="header-actions">
          {subscription && remainingQuotas.jobApplications !== null && (
            <div className="quota-info">
              <span className="quota-badge">
                {`${remainingQuotas.jobApplications} remaining`}
              </span>
            </div>
          )}
          <button
            className="add-button"
            onClick={handleAddApplication}
            title={
              addApplicationGated
                ? "Resume required — upload one to continue."
                : "Add a new job application"
            }
            data-tour-id="add-application-button"
          >
            <span className="button-icon">➕</span>
            Add Application
          </button>
        </div>
      </div>

      <div className="page-content" data-tour-id="applications-list">
        {isInitialApplicationsLoading ? (
          <div
            className="applications-loading-state"
            role="status"
            aria-live="polite"
          >
            <LoadingSpinner size="medium" />
            <p>Loading applications...</p>
          </div>
        ) : (
          <ApplicationsGrid
            applications={applications}
            onEditApplication={handleEditApplication}
            onViewApplication={handleViewApplication}
            onDeleteApplication={handleDeleteApplication}
            onUpdateStatus={handleUpdateStatus}
            onOpenNotes={handleOpenNotes}
            onResumeAssignment={handleResumeAssignment}
            onCommunicationCoach={handleCommunicationCoach}
            onCompanyIntelligence={handleOpenCompanyIntelligence}
            // onIntelligentSearch temporarily disabled
            // onIntelligentSearch={handleIntelligentSearch}
            onRefresh={loadApplications}
            isLoading={loadingStates.applications}
            isListTruncated={applicationsTruncated}
            displayLimit={JOB_APPLICATIONS_FETCH_LIMIT}
          />
        )}
      </div>

      {/* Wizard for adding new applications (includes Step 1: job description input) */}
      {isWizardOpen && !editingApplication && (
        <ApplicationWizard
          isOpen={isWizardOpen}
          onClose={handleCloseWizard}
          onSave={handleSaveApplication}
          editingApplication={null}
          viewMode={isViewMode}
        />
      )}

      {/* Standalone form for editing existing applications (skips Step 1) */}
      {isEditFormOpen && editingApplication && (
        <JobApplicationForm
          jobData={editingApplication}
          onSave={handleSaveApplication}
          onClose={handleCloseEditForm}
          viewMode={isViewMode}
          isProcessing={false}
          isSlider={true}
          modalTitle="Edit Application"
          onOpenCompanyIntelligence={handleOpenCompanyIntelligenceFromEdit}
        />
      )}

      {notesPanelOpen && selectedApplication && (
        <NotesPanel
          application={selectedApplication}
          onClose={handleCloseNotes}
          onSaveNote={handleSaveNote}
          onUpdateNote={handleUpdateNote}
          onDeleteNote={handleDeleteNote}
          onLoadNotes={handleLoadNotes}
        />
      )}

      {/* Intelligent Search Modal */}
      <IntelligentSearchModal
        isOpen={showIntelligentSearchModal}
        onClose={() => {
          setShowIntelligentSearchModal(false);
          setIntelligentSearchResults(null);
          setIntelligentSearchError(null);
        }}
        results={intelligentSearchResults}
        isLoading={intelligentSearchLoading}
        error={intelligentSearchError}
      />

      {/* Communication Coach Panel */}
      {isCommunicationCoachOpen && selectedApplicationForCoach && (
        <CommunicationCoachPanel
          application={selectedApplicationForCoach}
          onClose={handleCloseCommunicationCoach}
        />
      )}

      {/* Interview Prep Panel - Matches ResumeAssignmentModal pattern exactly */}
      {selectedApplicationForPrep && (
        <InterviewPrepPanel
          key={selectedApplicationForPrep.id || "no-id"}
          isOpen={interviewPrepPanelOpen}
          application={(() => {
            // Find the latest version from applications array to keep data in sync
            const appInArray = applications.find(
              (a) => a.id === selectedApplicationForPrep.id,
            );
            return appInArray || selectedApplicationForPrep;
          })()}
          onClose={() => {
            setInterviewPrepPanelOpen(false);
            setSelectedApplicationForPrep(null);
          }}
        />
      )}

      {/* Company Intelligence Panel */}
      {selectedApplicationForCompanyIntel && (
        <CompanyIntelligencePanel
          key={selectedApplicationForCompanyIntel.id || "no-id"}
          isOpen={companyIntelligencePanelOpen}
          application={(() => {
            if (shouldReopenEditAfterCompanyIntel) {
              return selectedApplicationForCompanyIntel;
            }
            // Find the latest version from applications array to keep data in sync
            const appInArray = applications.find(
              (a) => a.id === selectedApplicationForCompanyIntel.id,
            );
            return appInArray || selectedApplicationForCompanyIntel;
          })()}
          onClose={() => {
            setCompanyIntelligencePanelOpen(false);
            setSelectedApplicationForCompanyIntel(null);
            if (shouldReopenEditAfterCompanyIntel && editingApplication) {
              setIsEditFormOpen(true);
            }
            setShouldReopenEditAfterCompanyIntel(false);
          }}
        />
      )}

      {/* Resume Assignment Modal */}
      {selectedApplicationForResume && (
        <ResumeAssignmentModal
          isOpen={isResumeAssignmentOpen}
          onClose={() => {
            setIsResumeAssignmentOpen(false);
            setSelectedApplicationForResume(null);
          }}
          application={selectedApplicationForResume}
          onResumeUpdated={() => {
            loadApplications(false);
          }}
        />
      )}
    </div>
  );
};
